// ─────────────────────────────────────────────────────────────────────────────
// InterviewQuestions.tsx
// Drawer component that shows AI-generated interview questions for a job post.
// Sub-components are co-located in this file for easy readability.
// ─────────────────────────────────────────────────────────────────────────────

import {
  useGenerateInterviewQuestionsMutation,
  useGetAIGeneratedInterviewQuestionsQuery,
  useUpdateInterviewQuestionsMutation,
} from "@/app/features/recruitment/job-applicants/job-applicants.api";
import CustomDrawer from "@/components/CustomDrawer";
import WithLoader from "@/components/WithLoader";
import { AlertCircleIcon, LoadingIcon, StarsIcon } from "@/icons";
import * as H from "@hugeicons/core-free-icons";
import { HugeiconsIcon } from "@hugeicons/react";
import { toast } from "@/lib/toast";
import { getErrorMessage } from "@/lib/error";
import { cn } from "@/lib/cn";
import { Button, Flex, Form, Input, type FormInstance } from "antd";
import { useEffect, useState } from "react";

const { TextArea } = Input;

// ─── Types ────────────────────────────────────────────────────────────────────

interface Question {
  question_id: number;
  question: string;
  answer: string;
}

type QStatus = "pending" | "completed" | "failed" | null;

interface InterviewQuestionsProps {
  open: boolean;
  handleClose: () => void;
  id?: string;
  data?: {
    job_title: string;
    interview_qus_status: QStatus;
    questions: Question[];
  };
}

// ─── Sub-component: GeneratePrompt ────────────────────────────────────────────
// Shown when no questions exist yet. Lets the user trigger AI generation.

const AI_FEATURES = [
  {
    icon: H.Target01Icon,
    iconClass: "text-theme-primary",
    label: "Role-specific questions",
    desc: "Tailored to the exact job requirements",
  },
  {
    icon: H.BulbIcon,
    iconClass: "text-yellow-400",
    label: "Ideal answers included",
    desc: "Each question comes with a model answer",
  },
  {
    icon: H.FlashIcon,
    iconClass: "text-theme-primary",
    label: "Ready in seconds",
    desc: "Powered by advanced large language models",
  },
];

interface GeneratePromptProps {
  id?: string;
  /** Called immediately after the trigger API responds with success */
  onGenerate: () => void;
}

function GeneratePrompt({ id, onGenerate }: GeneratePromptProps) {
  const [triggerGenerate, { isLoading }] =
    useGenerateInterviewQuestionsMutation();

  const handleClick = async () => {
    if (!id) return;
    try {
      await triggerGenerate(id).unwrap();
      onGenerate(); // optimistically flip to "generating" UI
      toast("success", "AI is now generating interview questions…");
    } catch (error) {
      toast("error", getErrorMessage(error, "Failed to start AI generation."));
    }
  };

  return (
    <div className="flex flex-col items-center justify-center min-h-[500px] px-6 py-12">
      {/* Icon orb */}
      <div className="size-20 rounded-full bg-theme-primary/10 border border-theme-primary/20 flex items-center justify-center mb-6">
        <div className="size-12 rounded-full bg-theme-primary/15 border border-theme-primary/30 flex items-center justify-center">
          <StarsIcon size={24} className="text-theme-primary" />
        </div>
      </div>

      <h2 className="text-xl font-bold text-white text-center mb-2">
        No Interview Questions Yet
      </h2>
      <p className="text-sm text-theme-gray-400 text-center max-w-[400px] leading-relaxed mb-8">
        Let our AI engine craft a complete set of role-specific interview
        questions and model answers — saving your team hours of preparation.
      </p>

      {/* Feature list */}
      <div className="flex flex-col gap-2.5 w-full max-w-[400px] mb-8">
        {AI_FEATURES.map((f) => (
          <div
            key={f.label}
            className="flex items-center gap-3 border border-theme-gray-700/50 rounded-xl px-4 py-3"
          >
            <div className="shrink-0 size-8 rounded-lg bg-theme-gray-800 border border-theme-gray-700/60 flex items-center justify-center">
              <HugeiconsIcon
                icon={f.icon}
                size={16}
                className={f.iconClass}
                strokeWidth={1.8}
              />
            </div>
            <div>
              <p className="text-white text-sm font-semibold">{f.label}</p>
              <p className="text-theme-gray-500 text-xs">{f.desc}</p>
            </div>
          </div>
        ))}
      </div>

      <Button
        type="primary"
        size="large"
        loading={isLoading}
        onClick={handleClick}
        icon={!isLoading && <StarsIcon size={15} />}
        className="btn-primary h-11 px-8 font-semibold rounded-xl"
      >
        {isLoading ? "Starting…" : "Generate with AI"}
      </Button>

      <p className="text-xs text-theme-gray-600 mt-3 text-center">
        Typically completes in 15–30 seconds.
      </p>
    </div>
  );
}

// ─── Sub-component: GeneratingView ────────────────────────────────────────────
// Shown while AI is generating (status = "pending" or optimistic trigger).

function GeneratingView() {
  return (
    <div className="flex flex-col items-center justify-center min-h-[500px] gap-4">
      <div className="size-16 rounded-full bg-theme-primary/10 border border-theme-primary/20 flex items-center justify-center">
        <LoadingIcon size={28} className="text-theme-primary animate-spin" />
      </div>
      <h3 className="text-lg font-semibold text-white">
        AI Generation in Progress
      </h3>
      <p className="text-sm text-theme-gray-400 text-center max-w-[360px] leading-relaxed">
        Our AI engine is crafting the most relevant interview questions for this
        job post. This usually takes 15–30 seconds.
      </p>
    </div>
  );
}

// ─── Sub-component: FailedView ────────────────────────────────────────────────
// Shown when generation has failed.

function FailedView() {
  return (
    <div className="flex flex-col items-center justify-center min-h-[500px] gap-4">
      <div className="size-16 rounded-full bg-rose-500/10 border border-rose-500/20 flex items-center justify-center">
        <AlertCircleIcon size={28} className="text-rose-400" />
      </div>
      <h3 className="text-lg font-semibold text-white">Generation Failed</h3>
      <p className="text-sm text-theme-gray-400 text-center max-w-[360px] leading-relaxed">
        We couldn't generate interview questions at this moment. Please try
        closing and reopening the drawer to try again.
      </p>
    </div>
  );
}

// ─── Sub-component: QuestionList ─────────────────────────────────────────────
// Read-only display of completed questions.

interface QuestionListProps {
  questions: Question[];
  onEdit: () => void;
}

function QuestionList({ questions, onEdit }: QuestionListProps) {
  return (
    <div className="flex flex-col gap-0 pb-10">
      {questions.map((item, index) => (
        <div
          key={item.question_id}
          className={cn(
            "py-6",
            index !== questions.length - 1 &&
              "border-b border-dashed border-theme-gray-700",
          )}
        >
          {/* Question row */}
          <Flex align="start" gap={12}>
            <div className="shrink-0 size-7 rounded-full bg-theme-primary text-theme-slate-900 font-bold text-sm flex items-center justify-center mt-0.5">
              {index + 1}
            </div>
            <p className="text-white text-base font-semibold leading-snug flex-1">
              {item.question}
            </p>
          </Flex>

          {/* Ideal answer */}
          <div className="mt-3 ml-[2.375rem]">
            <p className="text-theme-primary text-xs font-semibold uppercase tracking-wider mb-2">
              Ideal Answer
            </p>
            <div className="text-theme-gray-300 text-sm leading-relaxed border border-theme-gray-700/40 rounded-lg px-4 py-3 bg-theme-gray-900/20">
              {item.answer}
            </div>
          </div>
        </div>
      ))}

      <Flex justify="end" className="mt-2">
        <Button size="large" className="btn-primary px-8" onClick={onEdit}>
          Edit Questions
        </Button>
      </Flex>
    </div>
  );
}

// ─── Sub-component: EditForm ──────────────────────────────────────────────────
// Editable form for modifying AI-generated questions before saving.

interface EditFormProps {
  form: FormInstance<any>;
  questions: Question[];
  isUpdating: boolean;
  onFinish: (values: { questions: Question[] }) => void;
  onCancel: () => void;
}

function EditForm({
  form,
  questions,
  isUpdating,
  onFinish,
  onCancel,
}: EditFormProps) {
  return (
    <Form
      form={form}
      size="large"
      layout="vertical"
      initialValues={{ questions }}
      onFinish={onFinish}
    >
      <Form.List name="questions">
        {(fields) => (
          <div className="flex flex-col gap-0 pb-10">
            {fields.map(({ key, name, ...rest }, index) => (
              <div
                key={key}
                className={cn(
                  "py-6",
                  index !== fields.length - 1 &&
                    "border-b border-dashed border-theme-gray-700",
                )}
              >
                {/* Hidden question_id */}
                <Form.Item {...rest} name={[name, "question_id"]} hidden>
                  <Input />
                </Form.Item>

                <Form.Item
                  {...rest}
                  name={[name, "question"]}
                  label={
                    <span className="text-white text-sm">
                      Question {index + 1}
                    </span>
                  }
                  rules={[{ required: true, message: "Question is required" }]}
                  className="mb-4"
                >
                  <TextArea
                    autoSize={{ minRows: 2 }}
                    className="transparent-input !text-white"
                    placeholder="Enter question…"
                  />
                </Form.Item>

                <Form.Item
                  {...rest}
                  name={[name, "answer"]}
                  label={
                    <span className="text-theme-primary text-sm">
                      Ideal Answer
                    </span>
                  }
                  rules={[{ required: true, message: "Answer is required" }]}
                  className="mb-0"
                >
                  <TextArea
                    autoSize={{ minRows: 4 }}
                    className="transparent-input !text-white"
                    placeholder="Enter ideal answer…"
                  />
                </Form.Item>
              </div>
            ))}
          </div>
        )}
      </Form.List>

      <Flex justify="end" gap={10} className="mt-2 mb-10">
        <Button className="btn-primary-bordered px-6" onClick={onCancel}>
          Cancel
        </Button>
        <Button
          type="primary"
          htmlType="submit"
          loading={isUpdating}
          className="btn-primary px-6"
        >
          Save Changes
        </Button>
      </Flex>
    </Form>
  );
}

// ─── Main Component ───────────────────────────────────────────────────────────
// Orchestrates state, polling, and sub-component rendering.

export default function InterviewQuestions({
  open,
  handleClose,
  id,
  data,
}: InterviewQuestionsProps) {
  const [form] = Form.useForm();
  const [isEditing, setIsEditing] = useState(false);
  const [pollingInterval, setPollingInterval] = useState(0);

  // Optimistic flag: set true immediately after trigger API succeeds so the
  // "generating" UI appears before the first poll confirms the new status.
  const [isGenerationTriggered, setIsGenerationTriggered] = useState(false);

  // ── API: Fetch current questions (fires on drawer open via skip) ──────────
  const {
    data: apiResponse,
    isLoading,
    isFetching,
  } = useGetAIGeneratedInterviewQuestionsQuery(id!, {
    skip: !id || !open,
    pollingInterval,
  });

  const [updateInterviewQuestions, { isLoading: isUpdating }] =
    useUpdateInterviewQuestionsMutation();

  // ── Polling logic based on status ─────────────────────────────────────────
  useEffect(() => {
    const status: QStatus = apiResponse?.data?.interview_qus_status ?? null;

    if (status === "pending") {
      // Backend confirmed generation is in progress — keep polling
      setPollingInterval(3000);
    } else if (status === "completed" || status === "failed") {
      // Done — stop polling, clear optimistic flag
      setPollingInterval(0);
      setIsGenerationTriggered(false);
    } else if (status === null && isGenerationTriggered) {
      // null means backend hasn't flipped to "pending" yet after trigger.
      // Keep polling until it does.
      setPollingInterval(3000);
    } else {
      // null status and not triggered — no generation running
      setPollingInterval(0);
    }
  }, [apiResponse, isGenerationTriggered]);

  // ── Sync form values when questions load/update ───────────────────────────
  useEffect(() => {
    const questions = apiResponse?.data?.questions ?? data?.questions ?? [];
    if (questions.length > 0) {
      form.setFieldsValue({ questions });
    }
  }, [apiResponse, data, form]);

  // ── Cleanup state on drawer close ─────────────────────────────────────────
  const handleDrawerClose = () => {
    setIsGenerationTriggered(false);
    setPollingInterval(0);
    setIsEditing(false);
    handleClose();
  };

  // ── Save edited questions ─────────────────────────────────────────────────
  const handleUpdate = async (values: { questions: Question[] }) => {
    try {
      await updateInterviewQuestions({ id: id!, body: values }).unwrap();
      toast("success", "Interview questions updated successfully");
      setIsEditing(false);
    } catch (error) {
      toast("error", getErrorMessage(error, "Failed to update questions"));
    }
  };

  // ── Derive state ──────────────────────────────────────────────────────────
  const interviewData = apiResponse?.data ?? data;
  const status: QStatus = interviewData?.interview_qus_status ?? null;
  const questions: Question[] = interviewData?.questions ?? [];

  // Treat as "generating" if optimistic flag is set OR if status is pending
  const isGenerating = isGenerationTriggered || status === "pending";

  // ── Render content based on current state ─────────────────────────────────
  const renderContent = () => {
    if (isGenerating) return <GeneratingView />;
    if (status === "failed") return <FailedView />;

    if (status === "completed" && questions.length > 0) {
      if (isEditing) {
        return (
          <EditForm
            form={form}
            questions={questions}
            isUpdating={isUpdating}
            onFinish={handleUpdate}
            onCancel={() => setIsEditing(false)}
          />
        );
      }
      return (
        <QuestionList questions={questions} onEdit={() => setIsEditing(true)} />
      );
    }

    // Default: null status or no questions — show generate CTA
    return (
      <GeneratePrompt
        id={id}
        onGenerate={() => {
          setIsGenerationTriggered(true);
          setPollingInterval(3000);
        }}
      />
    );
  };

  return (
    <CustomDrawer
      width={850}
      open={open}
      onClose={handleDrawerClose}
      title={interviewData?.job_title ?? "Interview Questions"}
    >
      <WithLoader
        isLoading={isLoading || isFetching}
        className={cn(
          "rounded-lg",
          (isLoading || isFetching) && "h-[calc(100vh_-_105px)]",
        )}
      >
        {renderContent()}
      </WithLoader>
    </CustomDrawer>
  );
}
